import { createContext, PropsWithChildren, ReactNode, useCallback, useContext, useState } from "react";
import Modal from "../../components/Modal";

type ModalElement = {
    content: JSX.Element | JSX.Element[],
    header?: ReactNode,
    footer?: ReactNode,
}

type ContextModalData = {
    toggle: () => void,
    show: (data: ModalElement) => void,
    hide: () => void,
}

const DEFAULT: ContextModalData = {
    toggle: () => { },
    show: () => { },
    hide: () => { },
};

const ModalContext = createContext<ContextModalData>(DEFAULT);

export const ModalProvider = ({ children }: PropsWithChildren<any>) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [header, setHeader] = useState<ReactNode>(undefined);
    const [content, setContent] = useState<JSX.Element | JSX.Element[]>(<></>);
    const [footer, setFooter] = useState<ReactNode>(undefined);

    const toggle = useCallback(() => {
        setIsOpen(isOpen => !isOpen);
    }, []);

    const show = useCallback((data: ModalElement) => {
        setContent(data.content);
        if (!!data.header) {
            setHeader(data.header);
        }
        if (!!data.footer) {
            setFooter(data.footer);
        }
        setIsOpen(true);
    }, []);

    const hide = useCallback(() => {
        setIsOpen(false);
        setContent(<></>);
        setHeader(undefined);
        setFooter(undefined);
    }, []);

    return (
        <ModalContext.Provider value={{
            toggle,
            show,
            hide
        }}>
            {children}
            <Modal isOpen={isOpen} >
                {!!header && <div className="modal-header">
                    {header}
                </div>}
                <div className="modal-body">
                    {content}
                </div>
                {!!footer && <div className="modal-footer">
                    {footer}
                </div>}
            </Modal>
        </ModalContext.Provider>
    );
}

export function useModal(): ContextModalData {
    const context = useContext(ModalContext);

    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    return context;
}